import React, { useEffect, useMemo, useState, useRef } from "react";
import { apiFetch, apiJson } from "../utils/api";
import { ALL_MENU_KEYS, MENU_AVAILABLE_ACTIONS, MENU_ITEMS, TEAM_MEMBER_DEFAULT_MENUS } from "../constants/menuConfig";
import SidePanel from "./SidePanel";
import "./UserManagement.css";

const DATE_ACCESS_MODULES = ["sales", "work", "purchase", "inventory", "dashboard"];
const createDefaultDateAccessPolicy = () => Object.fromEntries(
  DATE_ACCESS_MODULES.map(moduleKey => [moduleKey, { backdateLimit: 1, futureLimit: 1 }])
);
const normalizeDateLimit = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Math.floor(numeric);
};
const normalizeDateAccessPolicy = source => {
  const input = source && typeof source === "object" ? source : {};
  return Object.fromEntries(
    DATE_ACCESS_MODULES.map(moduleKey => {
      const modulePolicy = input[moduleKey] || {};
      return [moduleKey, {
        backdateLimit: normalizeDateLimit(modulePolicy.backdateLimit ?? 1),
        futureLimit: normalizeDateLimit(modulePolicy.futureLimit ?? 1)
      }];
    })
  );
};

const emptyForm = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  copyFromUserRef: "",
  role: "team_member",
  teamMemberPublicId: "",
  teamMemberName: "",
  allowedBranchPublicIds: [],
  isActive: true,
  allowedMenus: [...TEAM_MEMBER_DEFAULT_MENUS],
  menuActions: {},
  dateAccessPolicy: createDefaultDateAccessPolicy()
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    role: "all",
    status: "all"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const submitInFlightRef = useRef(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiJson("/api/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load users", err);
      setError(err.message || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const res = await apiFetch("/api/team-members");
      const data = await res.json();
      setTeamMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load team members for user setup", err);
      setTeamMembers([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadTeamMembers();
    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => setBranches(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error("Failed to load branches for user setup", err);
        setBranches([]);
      });
  }, []);

  const teamMemberOptions = useMemo(() => {
    const mapped = teamMembers
      .map(member => ({
        publicId: member.publicId,
        name: `${member.firstName || ""} ${member.lastName || ""}`.trim()
      }))
      .filter(item => item.publicId && item.name);

    if (
      form.teamMemberPublicId &&
      form.teamMemberName &&
      !mapped.some(item => item.publicId === form.teamMemberPublicId)
    ) {
      return [{ publicId: form.teamMemberPublicId, name: form.teamMemberName }, ...mapped];
    }

    return mapped;
  }, [teamMembers, form.teamMemberPublicId, form.teamMemberName]);

  const updateField = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const normalizeMenuActionsForCopy = source => {
    if (!source || typeof source !== "object") return {};
    return Object.keys(source).reduce((acc, key) => {
      const list = Array.isArray(source[key]) ? source[key] : [];
      acc[key] = [...new Set(list.map(item => String(item || "").trim()).filter(Boolean))];
      return acc;
    }, {});
  };

  const toggleMenu = menuKey => {
    setForm(prev => {
      const exists = prev.allowedMenus.includes(menuKey);
      return {
        ...prev,
        allowedMenus: exists ? prev.allowedMenus.filter(key => key !== menuKey) : [...prev.allowedMenus, menuKey]
      };
    });
  };

  const toggleAction = (menuKey, action) => {
    setForm(prev => {
      const current = prev.menuActions[menuKey] || [];
      const exists = current.includes(action);
      return {
        ...prev,
        menuActions: {
          ...prev.menuActions,
          [menuKey]: exists ? current.filter(a => a !== action) : [...current, action]
        }
      };
    });
  };

  const toggleBranch = branchPublicId => {
    setForm(prev => {
      const exists = prev.allowedBranchPublicIds.includes(branchPublicId);
      return {
        ...prev,
        allowedBranchPublicIds: exists
          ? prev.allowedBranchPublicIds.filter(id => id !== branchPublicId)
          : [...prev.allowedBranchPublicIds, branchPublicId]
      };
    });
  };

  const updateDateAccess = (moduleKey, fieldKey, rawValue) => {
    setForm(prev => ({
      ...prev,
      dateAccessPolicy: {
        ...(prev.dateAccessPolicy || createDefaultDateAccessPolicy()),
        [moduleKey]: {
          ...((prev.dateAccessPolicy || createDefaultDateAccessPolicy())[moduleKey] || { backdateLimit: 1, futureLimit: 1 }),
          [fieldKey]: normalizeDateLimit(rawValue)
        }
      }
    }));
  };

  const onRoleChange = role => {
    setForm(prev => ({
      ...prev,
      role,
      teamMemberPublicId: role === "team_member" ? (prev.teamMemberPublicId || "") : "",
      teamMemberName: role === "team_member"
        ? (prev.teamMemberName || "")
        : "",
      allowedMenus: role === "admin" ? [...ALL_MENU_KEYS] : (prev.allowedMenus.length ? prev.allowedMenus : [...TEAM_MEMBER_DEFAULT_MENUS])
    }));
  };

  const copyAccessFromUser = userRef => {
    setForm(prev => {
      if (!userRef) {
        return {
          ...prev,
          copyFromUserRef: ""
        };
      }

      const source = users.find(user => String(user.publicId || user.id) === userRef);
      if (!source) {
        return {
          ...prev,
          copyFromUserRef: ""
        };
      }

      const sourceRole = source.role === "admin" ? "admin" : "team_member";
      const sourceAllowedMenus = sourceRole === "admin"
        ? [...ALL_MENU_KEYS]
        : (Array.isArray(source.allowedMenus) && source.allowedMenus.length
          ? [...source.allowedMenus]
          : [...TEAM_MEMBER_DEFAULT_MENUS]);

      return {
        ...prev,
        copyFromUserRef: userRef,
        role: sourceRole,
        teamMemberPublicId: sourceRole === "team_member" ? prev.teamMemberPublicId : "",
        teamMemberName: sourceRole === "team_member" ? prev.teamMemberName : "",
        allowedBranchPublicIds: Array.isArray(source.allowedBranchPublicIds) ? [...source.allowedBranchPublicIds] : [],
        allowedMenus: sourceAllowedMenus,
        menuActions: normalizeMenuActionsForCopy(source.menuActions),
        dateAccessPolicy: normalizeDateAccessPolicy(source.dateAccessPolicy)
      };
    });
  };

  const submit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) return;
    
    submitInFlightRef.current = true;
    setIsSaving(true);

    const releaseSubmitLock = () => {
      submitInFlightRef.current = false;
      setIsSaving(false);
    };

    if (!form.firstName.trim() || !form.username.trim()) {
      alert("First name and username are required.");
      releaseSubmitLock();
      return;
    }

    if (!editingId && !form.password.trim()) {
      alert("Password is required for new users.");
      releaseSubmitLock();
      return;
    }

    if (form.role === "team_member" && form.allowedBranchPublicIds.length === 0) {
      alert("Select at least one allowed branch for team member users.");
      releaseSubmitLock();
      return;
    }

    const selectedTeamMember = teamMemberOptions.find(item => item.publicId === form.teamMemberPublicId);

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      username: form.username.trim().toLowerCase(),
      password: form.password,
      role: form.role,
      teamMemberPublicId: form.role === "team_member" ? form.teamMemberPublicId.trim() : "",
      teamMemberName: form.role === "team_member" ? (selectedTeamMember?.name || form.teamMemberName || "") : "",
      isActive: !!form.isActive,
      allowedBranchPublicIds: form.allowedBranchPublicIds,
      allowedMenus: form.role === "admin" ? [...ALL_MENU_KEYS] : form.allowedMenus,
      menuActions: form.menuActions,
      dateAccessPolicy: normalizeDateAccessPolicy(form.dateAccessPolicy)
    };

    try {
      let saved;
      if (editingId) {
        saved = await apiJson(`/api/users/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
        setUsers(prev => prev.map(user => ((user.publicId || user.id) === editingId ? saved : user)));
      } else {
        saved = await apiJson("/api/users", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        setUsers(prev => [saved, ...prev]);
      }

      setEditingId(null);
      setForm(emptyForm);
      setIsPanelOpen(false);
      releaseSubmitLock();
    } catch (err) {
      console.error("Failed to save user", err);
      alert(err.message || "Could not save user");
      releaseSubmitLock();
    }
  };

  const openEdit = user => {
    setEditingId(user.publicId || String(user.id));
    setIsPanelOpen(true);
    setForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      username: user.username || "",
      password: "",
      role: user.role || "team_member",
      teamMemberPublicId: user.teamMemberPublicId || "",
      teamMemberName: user.teamMemberName || "",
      allowedBranchPublicIds: Array.isArray(user.allowedBranchPublicIds) ? user.allowedBranchPublicIds : [],
      isActive: !!user.isActive,
      allowedMenus: Array.isArray(user.allowedMenus) ? user.allowedMenus : [...TEAM_MEMBER_DEFAULT_MENUS],
      menuActions: user.menuActions && typeof user.menuActions === "object" ? user.menuActions : {},
      dateAccessPolicy: normalizeDateAccessPolicy(user.dateAccessPolicy)
    });
  };

  const openCreatePanel = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsPanelOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsPanelOpen(false);
  };

  const deleteUser = async user => {
    const ref = String(user?.publicId || user?.id || "");
    if (!ref) return;
    const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || "this user";
    const ok = window.confirm(`Delete ${name}? This action cannot be undone.`);
    if (!ok) return;

    try {
      await apiJson(`/api/users/${ref}`, { method: "DELETE" });
      setUsers(prev => prev.filter(item => String(item.publicId || item.id) !== ref));
      if (editingId === ref) {
        resetForm();
      }
    } catch (err) {
      console.error("Failed to delete user", err);
      alert(err.message || "Could not delete user");
    }
  };

  const activeCount = useMemo(() => users.filter(user => user.isActive).length, [users]);
  const filteredUsers = useMemo(() => {
    const search = filters.query.trim().toLowerCase();
    return users.filter(user => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim().toLowerCase();
      const username = String(user.username || "").toLowerCase();
      const roleMatch = filters.role === "all" || user.role === filters.role;
      const statusMatch =
        filters.status === "all" ||
        (filters.status === "active" ? !!user.isActive : !user.isActive);
      const searchMatch =
        !search ||
        fullName.includes(search) ||
        username.includes(search) ||
        String(user.teamMemberName || "").toLowerCase().includes(search);
      return roleMatch && statusMatch && searchMatch;
    });
  }, [users, filters]);
  const filteredActiveCount = useMemo(() => filteredUsers.filter(user => user.isActive).length, [filteredUsers]);
  const filteredAdminCount = useMemo(() => filteredUsers.filter(user => user.role === "admin").length, [filteredUsers]);
  const branchCodeById = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => {
      const ref = String(branch.publicId || branch.id || "").trim();
      if (!ref) return;
      map.set(ref, String(branch.branchCode || branch.branchName || ref));
    });
    return map;
  }, [branches]);

  const updateFilter = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ query: "", role: "all", status: "all" });
  };

  return (
    <div className="users-page page main-content">
      <div className="users-header">
        <div>
          <h1>User Access Setup</h1>
          <p>Configure secure login access, menu permissions, and action-level controls.</p>
        </div>
        <div className="users-header-actions">
          <div className="users-summary">{`${activeCount} active users / ${users.length} total`}</div>
          <button type="button" className="open-panel-btn" onClick={openCreatePanel}>Create User</button>
        </div>
      </div>

      <section className="users-card users-filters-card">
        <div className="users-filter-row">
          <div className="users-filter-item users-filter-wide">
            <label htmlFor="usersFilterQuery">Search</label>
            <input
              id="usersFilterQuery"
              name="query"
              value={filters.query}
              onChange={updateFilter}
              placeholder="Name, username, or team member"
            />
          </div>
          <div className="users-filter-item">
            <label htmlFor="usersFilterRole">Role</label>
            <select id="usersFilterRole" name="role" value={filters.role} onChange={updateFilter}>
              <option value="all">All roles</option>
              <option value="admin">Admin</option>
              <option value="team_member">Team Member</option>
            </select>
          </div>
          <div className="users-filter-item">
            <label htmlFor="usersFilterStatus">Status</label>
            <select id="usersFilterStatus" name="status" value={filters.status} onChange={updateFilter}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Disabled</option>
            </select>
          </div>
          <div className="users-filter-item users-filter-action">
            <button type="button" className="open-panel-btn" onClick={clearFilters}>Clear</button>
          </div>
        </div>
      </section>

      <section className="users-grid">
        <div className="users-card users-list-card">
          {loading ? <p>Loading users...</p> : error ? <p className="users-error">{error}</p> : (
            <div className="users-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Branches</th>
                    <th>Menus</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="users-empty-cell">No users found for current filters.</td>
                    </tr>
                  )}
                  {filteredUsers.map(user => (
                    <tr key={user.publicId || user.id}>
                      <td>{`${user.firstName || ""} ${user.lastName || ""}`.trim()}</td>
                      <td>{user.username}</td>
                      <td>{user.role === "admin" ? "Admin" : "Team Member"}</td>
                      <td>{user.isActive ? "Active" : "Disabled"}</td>
                      <td>
                        {(user.allowedBranchPublicIds || [])
                          .map(id => branchCodeById.get(String(id || "").trim()) || String(id || ""))
                          .filter(Boolean)
                          .join(", ")}
                      </td>
                      <td>{(user.allowedMenus || []).join(", ")}</td>
                      <td className="users-actions-cell">
                        <div className="users-row-actions">
                          <button
                            type="button"
                            className="users-icon-btn users-edit-btn"
                            title="Edit"
                            aria-label="Edit user"
                            onClick={() => openEdit(user)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="#fff" opacity="0.9"/>
                              <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="#fff" opacity="0.9"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="users-icon-btn users-delete-btn"
                            title="Delete"
                            aria-label="Delete user"
                            onClick={() => deleteUser(user)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                              <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z" fill="#fff" opacity="0.95"/>
                              <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#fff" opacity="0.95"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="users-card users-totals-card">
        <div className="users-totals-grid">
          <div className="users-total-item">
            <span>Filtered users</span>
            <strong>{filteredUsers.length}</strong>
          </div>
          <div className="users-total-item">
            <span>Active users</span>
            <strong>{filteredActiveCount}</strong>
          </div>
          <div className="users-total-item">
            <span>Admins</span>
            <strong>{filteredAdminCount}</strong>
          </div>
          <div className="users-total-item">
            <span>Team members</span>
            <strong>{Math.max(filteredUsers.length - filteredAdminCount, 0)}</strong>
          </div>
        </div>
      </section>

      <SidePanel isOpen={isPanelOpen} onClose={resetForm} className="panel-wide">
        <div className="users-form-container">
          <form onSubmit={submit} className="users-form">
            <div className="users-form-row two-col">
              <div>
                <label>First name *</label>
                <input name="firstName" value={form.firstName} onChange={updateField} required />
              </div>
              <div>
                <label>Last name</label>
                <input name="lastName" value={form.lastName} onChange={updateField} />
              </div>
            </div>

            <div className="users-form-row two-col">
              <div>
                <label>Username *</label>
                <input name="username" value={form.username} onChange={updateField} required />
              </div>
              <div>
                <label>{editingId ? "New password (optional)" : "Password *"}</label>
                <input name="password" type="password" value={form.password} onChange={updateField} required={!editingId} />
              </div>
            </div>

            <div className="users-form-row two-col">
              <div>
                <label>Role *</label>
                <select name="role" value={form.role} onChange={e => onRoleChange(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="team_member">Team Member</option>
                </select>
              </div>
              <div>
                <label>Team Member Link</label>
                {form.role === "team_member" ? (
                  <select
                    name="teamMemberPublicId"
                    value={form.teamMemberPublicId}
                    onChange={e => {
                      const selected = teamMemberOptions.find(item => item.publicId === e.target.value);
                      setForm(prev => ({
                        ...prev,
                        teamMemberPublicId: e.target.value,
                        teamMemberName: selected?.name || ""
                      }));
                    }}
                  >
                    <option value="">No linked team member (can access all team member data)</option>
                    {teamMemberOptions.map(item => (
                      <option key={item.publicId} value={item.publicId}>{item.name}</option>
                    ))}
                  </select>
                ) : (
                  <input name="teamMemberName" value="Not required for admin" readOnly />
                )}
              </div>
            </div>

            {!editingId && (
              <div className="users-form-row">
                <label>Copy Access From User</label>
                <select
                  name="copyFromUserRef"
                  value={form.copyFromUserRef}
                  onChange={e => copyAccessFromUser(e.target.value)}
                >
                  <option value="">Do not copy</option>
                  {users.map(user => {
                    const ref = String(user.publicId || user.id);
                    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username;
                    const roleText = user.role === "admin" ? "Admin" : "Team Member";
                    return (
                      <option key={ref} value={ref}>{`${name} (${user.username}) - ${roleText}`}</option>
                    );
                  })}
                </select>
              </div>
            )}

            <div className="users-active-row">
              <label>
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={updateField} /> Active user
              </label>
            </div>

            <div className="users-menu-block">
              <h3>Allowed Branches</h3>
              <p>Sales and stock access for this user will be limited to these branches.</p>
              <div className="users-menu-grid">
                {branches.map(branch => (
                  <label key={branch.publicId || branch.id} className="menu-check">
                    <input
                      type="checkbox"
                      checked={form.allowedBranchPublicIds.includes(branch.publicId)}
                      onChange={() => toggleBranch(branch.publicId)}
                    />
                    {`${branch.branchCode || ""} - ${branch.branchName || "Branch"}`}
                  </label>
                ))}
              </div>
            </div>

            <div className="users-menu-block">
              <h3>Date Access Policy (Per Module)</h3>
              <p>`0 = unlimited`, `1 = today only`, `2 = today + 1 day`.</p>
              <div className="users-menu-list">
                {DATE_ACCESS_MODULES.map(moduleKey => {
                  const modulePolicy = form.dateAccessPolicy?.[moduleKey] || { backdateLimit: 1, futureLimit: 1 };
                  return (
                    <div key={moduleKey} className="users-form-row two-col">
                      <div>
                        <label>{`${moduleKey.charAt(0).toUpperCase() + moduleKey.slice(1)} backdate limit`}</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={modulePolicy.backdateLimit}
                          onChange={e => updateDateAccess(moduleKey, "backdateLimit", e.target.value)}
                        />
                      </div>
                      <div>
                        <label>{`${moduleKey.charAt(0).toUpperCase() + moduleKey.slice(1)} future limit`}</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={modulePolicy.futureLimit}
                          onChange={e => updateDateAccess(moduleKey, "futureLimit", e.target.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="users-menu-block">
              <h3>Available Menus &amp; Actions</h3>
              <p>Choose which pages this user can open and which actions they can perform.</p>
              <div className="users-menu-list">
                {MENU_ITEMS.map(item => {
                  const isMenuEnabled = form.role === "admin" || form.allowedMenus.includes(item.key);
                  const availableActions = MENU_AVAILABLE_ACTIONS[item.key] || [];
                  return (
                    <div key={item.key} className="menu-check-group">
                      <label className="menu-check menu-check-parent">
                        <input
                          type="checkbox"
                          checked={isMenuEnabled}
                          onChange={() => toggleMenu(item.key)}
                          disabled={form.role === "admin"}
                        />
                        <span className="menu-check-label">{item.label}</span>
                      </label>
                      {availableActions.length > 0 && isMenuEnabled && (
                        <div className="menu-actions-row">
                          {availableActions.map(action => (
                            <label key={action} className="action-check">
                              <input
                                type="checkbox"
                                checked={form.role === "admin" || (form.menuActions[item.key] || []).includes(action)}
                                onChange={() => toggleAction(item.key, action)}
                                disabled={form.role === "admin"}
                              />
                              {action.charAt(0).toUpperCase() + action.slice(1)}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="users-form-actions">
              <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : (editingId ? "Save User" : "Create User")}</button>
              {editingId && <button type="button" onClick={resetForm}>Cancel Edit</button>}
            </div>
          </form>
        </div>
      </SidePanel>
    </div>
  );
};

export default UserManagement;
